import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  AccessTime,
  People,
  AttachMoney,
  Refresh,
} from "@mui/icons-material";

const PaymentRequestDialog = ({
  open,
  onClose,
  message,
  currentUserId,
  onPaymentResponse,
  onRetryRequest,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [hasResponded, setHasResponded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const orderData = message?.orderId;
  const paymentFlow = orderData?.paymentFlow;
  const request = paymentFlow?.request;
  const responses = request?.responses || [];
  const initiatedBy = paymentFlow?.initiatedBy;
  const isInitiator = initiatedBy === currentUserId;

  // Check if current user has already responded
  const currentUserResponse = responses.find(
    response => response.userId === currentUserId
  );

  // Timer effect
  useEffect(() => {
    if (open && timeRemaining > 0 && !hasResponded) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [open, timeRemaining, hasResponded]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setTimeRemaining(30);
      setHasResponded(!!currentUserResponse);
    }
  }, [open, currentUserResponse]);

  // Auto-respond if timer expires
  useEffect(() => {
    if (timeRemaining === 0 && !hasResponded && !isInitiator) {
      handleResponse("rejected");
    }
  }, [timeRemaining, hasResponded, isInitiator]);

  const handleResponse = async (response) => {
    if (hasResponded || isInitiator) return;

    setIsProcessing(true);
    try {
      if (onPaymentResponse) {
        await onPaymentResponse({
          orderId: orderData._id,
          response: response,
          userId: currentUserId,
        });
      }
      setHasResponded(true);
    } catch (error) {
      console.error("Error responding to payment request:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetry = async () => {
    setIsProcessing(true);
    try {
      if (onRetryRequest) {
        await onRetryRequest({
          orderId: orderData._id,
          initiatorId: currentUserId,
        });
      }
      onClose();
    } catch (error) {
      console.error("Error retrying payment request:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getResponseStatus = (userId) => {
    const response = responses.find(r => r.userId === userId);
    if (!response) return "pending";
    return response.response;
  };

  const getResponseIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle sx={{ color: "#10B981" }} />;
      case "rejected":
        return <Cancel sx={{ color: "#EF4444" }} />;
      default:
        return <AccessTime sx={{ color: "#F59E0B" }} />;
    }
  };

  const getResponseColor = (status) => {
    switch (status) {
      case "approved":
        return "#10B981";
      case "rejected":
        return "#EF4444";
      default:
        return "#F59E0B";
    }
  };

  const calculateAmounts = () => {
    if (!orderData?.groupOrder?.userOrders) {
      return {
        individualAmounts: [],
        totalUpfront: 0,
        initiatorAmount: 0,
        participantAmount: 0,
      };
    }

    const userOrders = orderData.groupOrder.userOrders;
    const upfrontPercentage = orderData.paymentSettings?.upfrontPercentage || 20;
    
    const individualAmounts = userOrders.map(userOrder => ({
      userId: userOrder.userId,
      userName: userOrder.userFullName,
      amount: ((userOrder.subtotal || 0) * upfrontPercentage) / 100,
      subtotal: userOrder.subtotal || 0,
    }));

    const totalUpfront = individualAmounts.reduce((sum, user) => sum + (user.amount || 0), 0);
    
    // Calculate based on strategy
    const strategy = request?.paymentType;
    let initiatorAmount = 0;
    let participantAmount = 0;

    if (strategy === "pay_for_all") {
      // Initiator pays for all approved users who haven't paid upfront
      const approvedUsers = responses.filter(r => r.response === "approved");
      const unpaidApprovedUsers = approvedUsers.filter(response => {
        const userOrder = userOrders.find(u => u.userId === response.userId);
        return userOrder && (userOrder.amountPaid === 0 || userOrder.paymentStatus !== "upfront_paid");
      });
      
      initiatorAmount = unpaidApprovedUsers.reduce((sum, response) => {
        const userOrder = userOrders.find(u => u.userId === response.userId);
        return sum + ((userOrder?.subtotal || 0) * upfrontPercentage) / 100;
      }, 0);
      
      // Add initiator's own amount if they haven't paid upfront
      const initiatorOrder = userOrders.find(u => u.userId === initiatedBy);
      if (initiatorOrder && (initiatorOrder.amountPaid === 0 || initiatorOrder.paymentStatus !== "upfront_paid")) {
        initiatorAmount += ((initiatorOrder?.subtotal || 0) * upfrontPercentage) / 100;
      }
    } else if (strategy === "split_evenly") {
      // Split evenly among approved users
      const approvedUsers = responses.filter(r => r.response === "approved");
      const totalUsers = approvedUsers.length + 1; // +1 for initiator
      participantAmount = totalUsers > 0 ? totalUpfront / totalUsers : 0;
      initiatorAmount = participantAmount;
    }

    return {
      individualAmounts,
      totalUpfront,
      initiatorAmount,
      participantAmount,
    };
  };

  const amounts = calculateAmounts();

  if (isInitiator) {
    // Initiator view
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            pb: 1,
            background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
            color: "white",
            fontWeight: 700,
            fontSize: "1.5rem",
          }}
        >
          Payment Request Status
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Timer */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Time remaining:
              </Typography>
              <Chip
                label={`${timeRemaining}s`}
                color={timeRemaining <= 10 ? "error" : "primary"}
                size="small"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={(timeRemaining / 30) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "#E5E7EB",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: timeRemaining <= 10 ? "#EF4444" : "#7C3AED",
                },
              }}
            />
          </Box>

          {/* Payment summary */}
          <Card sx={{ mb: 3, backgroundColor: "#F8FAFC" }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Payment Summary
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2">Your amount:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ${amounts.initiatorAmount.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Total upfront:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ${amounts.totalUpfront.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Response list */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Responses
          </Typography>
          <List>
            {orderData?.groupOrder?.userOrders
              ?.filter(userOrder => userOrder.userId !== initiatedBy)
              .map((userOrder) => {
                const status = getResponseStatus(userOrder.userId);
                return (
                  <ListItem key={userOrder.userId} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: getResponseColor(status) }}>
                        {getResponseIcon(status)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={userOrder.userFullName}
                      secondary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={status}
                            size="small"
                            sx={{
                              backgroundColor: getResponseColor(status),
                              color: "white",
                              textTransform: "capitalize",
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            ${((userOrder.subtotal * (orderData.paymentSettings?.upfrontPercentage || 20)) / 100).toFixed(2)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
          </List>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ mr: 2, textTransform: "none" }}
          >
            Close
          </Button>
          <Button
            onClick={handleRetry}
            variant="contained"
            disabled={isProcessing}
            startIcon={<Refresh />}
            sx={{
              textTransform: "none",
              background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #6D28D9 0%, #9333EA 100%)",
              },
            }}
          >
            {isProcessing ? "Processing..." : "Try Again"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Recipient view
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pb: 1,
          background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
          color: "white",
          fontWeight: 700,
          fontSize: "1.5rem",
        }}
      >
        Payment Request
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Timer */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Time remaining:
            </Typography>
            <Chip
              label={`${timeRemaining}s`}
              color={timeRemaining <= 10 ? "error" : "primary"}
              size="small"
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={(timeRemaining / 30) * 100}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "#E5E7EB",
              "& .MuiLinearProgress-bar": {
                backgroundColor: timeRemaining <= 10 ? "#EF4444" : "#7C3AED",
              },
            }}
          />
        </Box>

        {/* Request info */}
        <Card sx={{ mb: 3, backgroundColor: "#F8FAFC" }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Payment Request
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {request?.paymentType === "pay_for_all" 
                ? "The initiator wants to pay for everyone's upfront amount."
                : "The initiator wants to split the upfront amount evenly among all participants."
              }
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Your amount:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ${amounts.participantAmount.toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Response status */}
        {hasResponded && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: "#F0FDF4", borderRadius: 2 }}>
            <Typography variant="body2" color="#059669" sx={{ textAlign: "center" }}>
              ✓ You have responded to this request
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ mr: 2, textTransform: "none" }}
        >
          Close
        </Button>
        {!hasResponded && (
          <>
            <Button
              onClick={() => handleResponse("rejected")}
              variant="outlined"
              color="error"
              disabled={isProcessing}
              startIcon={<Cancel />}
              sx={{ textTransform: "none" }}
            >
              {isProcessing ? "Processing..." : "Reject"}
            </Button>
            <Button
              onClick={() => handleResponse("approved")}
              variant="contained"
              color="success"
              disabled={isProcessing}
              startIcon={<CheckCircle />}
              sx={{
                textTransform: "none",
                background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                },
              }}
            >
              {isProcessing ? "Processing..." : "Accept"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentRequestDialog;
